import type { WidgetProps } from '@rjsf/utils';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { IMAGE_FILE_EXTENSIONS } from '../../../constants/file-extensions';
import { TypographyVariant } from '../../../utils/theme';
import { getFileAsDataUrlWithName } from '../../../utils/get-file-as-data-url-with-name';
import FileUploaderAndList from '../../file-uploader-and-list/file-uploader-and-list';
import type { IFileUploader } from '../../file-uploader/file-uploader';
import ImageCropper from '../../image-cropper/image-cropper';
import InputLabel from '../../input-label/input-label';
import type { ModalRef } from '../../modals';
import { Modal } from '../../modals';
import Typography from '../../typography/typography';
import { extractFileNameFromDataUrl, parseExistingFile } from '../utils';

interface ImageCropperWidgetProps extends Omit<WidgetProps, 'value'> {
  value?: string;
}

const MAX_FILES_NUMBER = 1;

const ImageCropperWidget = ({
  value,
  label,
  onChange,
  disabled,
  rawErrors,
  hideError,
  uiSchema,
  registry,
  schema,
  readonly,
}: ImageCropperWidgetProps) => {
  const formContext = registry?.formContext;
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [croppedImage, setCroppedImage] = useState<string>();
  const [imageFile, setImageFile] = useState<File>();
  const { t } = useTranslation();
  const fileId = useId();

  const hasError = Boolean(rawErrors && rawErrors.length > 0 && !hideError);
  const imageCropperModalRef = useRef<ModalRef>(null);

  const handleFileUpload = (file: File) => {
    setImageFile(file);
    setIsUploading(true);
    // handling the initial crop of the image before modal is shown
    void handleCropComplete(file).then(() => {
      imageCropperModalRef?.current?.showModal();
    });
  };

  const files = useMemo(() => {
    if (!value) return [];
    if (value?.startsWith('data:text/url')) {
      return [parseExistingFile(value)];
    }
    return [
      {
        file: value,
        uuid: fileId,
        originalName: extractFileNameFromDataUrl(value, Number(fileId)),
      },
    ];
  }, [value, fileId]);

  const handleRemove = useCallback(() => {
    onChange(undefined);
    setCroppedImage(undefined);
    setImageFile(undefined);
    setIsUploading(false);
  }, [onChange, setIsUploading]);

  const fileUploaderProps: IFileUploader = {
    uploading: isUploading,
    onFileUpload: (files) => handleFileUpload(files[0]),
    acceptedFileExtensions:
      uiSchema?.acceptFileTypes ??
      formContext?.acceptFileTypes ??
      IMAGE_FILE_EXTENSIONS,
    uploadError: hasError ? rawErrors?.join(', ') : undefined,
    disabled: disabled || readonly,
    maxFilesNumber: MAX_FILES_NUMBER,
    name: label,
  };

  const handleCropComplete = useCallback(
    async (croppedImageResult: File) => {
      const fileAsDataUrl = await getFileAsDataUrlWithName(croppedImageResult);
      setCroppedImage(fileAsDataUrl as string);
    },
    [setCroppedImage]
  );

  const handleImageSave = useCallback(() => {
    if (!croppedImage) return;
    onChange(croppedImage);
    setIsUploading(false);
    imageCropperModalRef?.current?.hideModal();
  }, [onChange, setIsUploading, croppedImage]);

  return (
    <>
      <Wrapper>
        <InputLabel theme={theme} $hasError={hasError}>
          {label}
        </InputLabel>
        <Typography
          variant={TypographyVariant.bodySm1}
          color={theme.palette.neutral[90]}
        >
          {schema?.description}
        </Typography>
        <InputWrapper>
          <FileUploaderAndList
            fileUploaderProps={fileUploaderProps}
            files={files}
            onRemove={handleRemove}
            enableImagePreview={true}
            imagePreviewWidth={uiSchema?.imagePreviewWidth}
            imagePreviewHeight={uiSchema?.imagePreviewHeight}
          />
        </InputWrapper>
      </Wrapper>
      <Modal
        ref={imageCropperModalRef}
        title={t('cropModal.title')}
        primaryText={t('phrases.saveChanges', { defaultValue: 'Save changes' })}
        secondaryText={t('words.cancel', { defaultValue: 'Cancel' })}
        width="auto"
        onConfirm={handleImageSave}
        onClose={() => {
          handleRemove();
          imageCropperModalRef?.current?.hideModal();
        }}
      >
        {imageFile && (
          <ImageCropperWrapper>
            <StyledImageCropper
              src={imageFile}
              onCropComplete={handleCropComplete}
              aspectRatio={uiSchema?.imageAspectRatio ?? 1}
            />
          </ImageCropperWrapper>
        )}
      </Modal>
    </>
  );
};

export default ImageCropperWidget;

export const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

export const StyledImageCropper = styled(ImageCropper)`
  height: 380px;
  width: 440px;
`;

export const ImageCropperWrapper = styled.div`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(3)};
  `}
`;

export const InputWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;
